import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { BOUsersSelection } from "./boUsersSelection";
/* eslint-disable import/export */
export abstract class DeleteProjectBOUsersRPC implements
  LeoRPC<DeleteProjectBOUsersRPC.Request, DeleteProjectBOUsersRPC.Response, DeleteProjectBOUsersRPC.Errors.Errors>
{
  abstract execute(
    request: DeleteProjectBOUsersRPC.Request
  ): Promise<LeoRPCResult<DeleteProjectBOUsersRPC.Response, DeleteProjectBOUsersRPC.Errors.Errors>>;
}

export namespace DeleteProjectBOUsersRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    boUsersSelection: BOUsersSelection.BOUsersSelectionDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isDeleteProjectBOUsersRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly boUsersSelection: BOUsersSelection.BOUsersSelection;
  
  
    constructor(
    projectId: LeoUUID, 
    boUsersSelection: BOUsersSelection.BOUsersSelection
    ) {
      this.projectId = projectId;
      this.boUsersSelection = boUsersSelection;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const boUsersSelection = BOUsersSelection.fromDTO(getObject(dto, "boUsersSelection", InvalidRequestBoUsersSelectionError));
      return new Request(
        projectId,
        boUsersSelection
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          boUsersSelection: this.boUsersSelection.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `DeleteProjectBOUsersRPC(projectId='${this.projectId}',boUsersSelection='${this.boUsersSelection}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `DeleteProjectBOUsersRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    InvalidBoUserId = "INVALID_BO_USER_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `DeleteProjectBOUsersRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `DeleteProjectBOUsersRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface InvalidBoUserIdDTO {
      code: string;
    }
    
    export class InvalidBoUserId extends LeoRPCError<RPCError> {
      readonly isInvalidBoUserId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidBoUserId;
        super(code);
      }
    
    
      toDTO(): InvalidBoUserIdDTO {
        const dto: InvalidBoUserIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidBoUserId.prototype.toString = function toString(): string {
      return `DeleteProjectBOUsersRPC.INVALID_BO_USER_ID()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived | InvalidBoUserId;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | InvalidBoUserIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.InvalidBoUserId:
          return new InvalidBoUserId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends DeleteProjectBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestBoUsersSelectionError extends DeleteProjectBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestBoUsersSelectionError";
  }
}


export class InvalidResponseInvalidProjectIdError extends DeleteProjectBOUsersRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends DeleteProjectBOUsersRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseInvalidBoUserIdError extends DeleteProjectBOUsersRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidBoUserIdError";
  }
}

